/**
 * Shared helpers for rendering CardBackground values as CSS.
 * Used by the canvas renderer (CardGridCoverDisplay) and the editor row swatch (SortableCardGridRow).
 */

import type { CSSProperties } from "react";
import { isValidHexColor } from "@/features/landing-pages/_logic/color-utils";
import { pickReadableOnBackground } from "@/lib/color/contrast";
import {
  SECTION_BG_IMAGE_DEFAULT_OVERLAY_OPACITY,
  SECTION_BG_IMAGE_OVERLAY_OPACITY_MAX,
  SECTION_BG_IMAGE_OVERLAY_OPACITY_MIN,
  SECTION_BG_IMAGE_SCRIM_DARK_HEX,
  SECTION_BG_IMAGE_SCRIM_LIGHT_HEX,
} from "./constants";
import type { CardBackground } from "../_types";

/**
 * Normalizes a hex color to always carry its `#` before it's embedded in a CSS string.
 * `isValidHexColor` accepts a bare `rrggbb` (no `#`) as valid input, but `background-color:
 * rrggbb` (missing the `#`) isn't valid CSS and the browser silently drops it — while any
 * contrast math elsewhere still treats the stored value as the intended color, producing text
 * colored for a background that never actually rendered. Only call with an already-validated hex.
 * @param hex - A hex color already confirmed valid by `isValidHexColor`.
 * @returns The same color, guaranteed to start with `#`.
 */
export function toCssHex(hex: string): string {
  return hex.startsWith("#") ? hex : `#${hex}`;
}

/**
 * Returns inline-style CSS for a non-image CardBackground (solid / brand gradient / custom gradient).
 * Returns `null` for kinds that should be rendered as a separate `<img>` element (image kind),
 * for "none" / missing values (callers handle those with their own placeholder), or when a color
 * value isn't a valid hex — these values reach an inline `style` prop verbatim, and React doesn't
 * escape `;` inside a style attribute, so an unvalidated string could inject arbitrary CSS
 * declarations (e.g. a fixed-position overlay) into the public page.
 * @param background - the background to render
 * @param primaryColor - the page's primary color, used for the brand gradient
 * @returns React.CSSProperties suitable for a positioned background layer, or null when no inline CSS applies
 */
export function getBackgroundStyle(background: CardBackground | undefined, primaryColor: string): CSSProperties | null {
  if (!background) return null;
  if (background.kind === "solid") {
    return isValidHexColor(background.color) ? { backgroundColor: toCssHex(background.color) } : null;
  }
  if (background.kind === "brandGradient") {
    if (!isValidHexColor(primaryColor)) return null;
    const color = toCssHex(primaryColor);
    return { background: `linear-gradient(135deg, ${color}, ${color}88)` };
  }
  if (background.kind === "customGradient") {
    return isValidHexColor(background.from) && isValidHexColor(background.to)
      ? { background: `linear-gradient(135deg, ${toCssHex(background.from)}, ${toCssHex(background.to)})` }
      : null;
  }
  return null;
}

/**
 * Resolves an image background's overlay-scrim opacity to a valid 0-100 number: the stored
 * `overlayOpacity` when it's a finite number in range, or `SECTION_BG_IMAGE_DEFAULT_OVERLAY_OPACITY`
 * for anything else (unset, out of range, or malformed) — so a bad/legacy stored value degrades to
 * the original fixed-scrim look instead of producing invalid CSS or a NaN alpha.
 * @param background - the background to inspect (only the "image" kind carries this field)
 * @returns An overlay opacity in [0, 100].
 */
export function resolveOverlayOpacity(background: CardBackground | undefined): number {
  const value = background?.kind === "image" ? background.overlayOpacity : undefined;
  if (
    typeof value !== "number" ||
    !Number.isFinite(value) ||
    value < SECTION_BG_IMAGE_OVERLAY_OPACITY_MIN ||
    value > SECTION_BG_IMAGE_OVERLAY_OPACITY_MAX
  ) {
    return SECTION_BG_IMAGE_DEFAULT_OVERLAY_OPACITY;
  }
  return value;
}

/**
 * Picks the tone of the legibility scrim composited under a section's background image: a light
 * wash under dark text, a dark wash under light text. Shared so the canvas renderer and the
 * editor's preview can't drift to opposite tones for the same section.
 * @param pageTextColor - The page's configured text color.
 * @returns The scrim's hex tone (opacity is applied separately, per section).
 */
export function resolveScrimHex(pageTextColor: string): string {
  // `pickReadableOnBackground` already returns one of the two arguments — it returns `darkOption`
  // for an unparseable color, which is the right default here too (a dark wash under the light
  // text such a page would fall back to).
  return pickReadableOnBackground(pageTextColor, SECTION_BG_IMAGE_SCRIM_LIGHT_HEX, SECTION_BG_IMAGE_SCRIM_DARK_HEX);
}

/**
 * Checks whether a background value represents an image with a usable URL.
 * Returns a plain boolean (not a type guard) so callers can still narrow
 * the remaining union after a negative check.
 * @param background - the background to inspect
 * @returns true when kind is "image" and the url is non-empty
 */
export function hasImageUrl(background: CardBackground | undefined): boolean {
  return background?.kind === "image" && !!background.url;
}
